import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import BackButton from '../../components/ui/BackButton';
import { drawingService } from '../../services/drawingService';
import { snagService } from '../../services/snagService';
import DrawingViewerPanel from './components/DrawingViewer';
import MetadataPanel from './components/MetadataPanel';
import RevisionHistory from './components/RevisionHistory';
import RelatedSnags from './components/RelatedSnags';
import UploadRevision from './components/UploadRevision';
import { useLanguage } from '../../contexts/LanguageContext';

const DrawingDetail = () => {
  const { drawingId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [drawing, setDrawing] = useState(null);
  const [drawingUrl, setDrawingUrl] = useState(null);
  const [isPdf, setIsPdf] = useState(false);
  const [versions, setVersions] = useState([]);
  const [snags, setSnags] = useState([]);

  const [loadingDrawing, setLoadingDrawing] = useState(true);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [loadingSnags, setLoadingSnags] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (drawingId) loadDrawing();
  }, [drawingId]);

  const loadDrawing = async () => {
    setLoadingDrawing(true);
    setError('');
    try {
      const { data, error: drawingError } = await drawingService?.getDrawingById(drawingId);
      if (drawingError) throw new Error(drawingError?.message || t('drawingNotFound'));
      if (!data) throw new Error(t('drawingNotFound'));

      setDrawing(data);
      setIsPdf(data?.mime_type?.includes('pdf'));

      if (data?.file_path) {
        const { url } = await drawingService?.getDrawingFileUrl(data?.file_path);
        setDrawingUrl(url);
      }

      loadVersions(data?.site_id, data?.drawing_number);
      loadSnags(drawingId);
    } catch (err) {
      console.error('[DrawingDetail] load drawing failed:', err);
      setError(t('failedToLoadDrawing'));
    } finally {
      setLoadingDrawing(false);
    }
  };

  const loadVersions = async (siteId, drawingNumber) => {
    if (!siteId || !drawingNumber) return;
    setLoadingVersions(true);
    try {
      const { data } = await drawingService?.getDrawingVersions(siteId, drawingNumber);
      setVersions(data || []);
    } finally {
      setLoadingVersions(false);
    }
  };

  const loadSnags = async (id) => {
    setLoadingSnags(true);
    try {
      const { data } = await snagService?.getSnagsByDrawing(id);
      setSnags(data || []);
    } finally {
      setLoadingSnags(false);
    }
  };

  const handleRevisionUploaded = (newDrawing) => {
    if (drawing?.site_id && drawing?.drawing_number) {
      loadVersions(drawing?.site_id, drawing?.drawing_number);
    }
    if (newDrawing?.id) {
      navigate(`/drawing-detail/${newDrawing?.id}`, { replace: true });
    }
  };

  if (loadingDrawing) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex flex-col items-center justify-center h-[calc(100vh-80px)] gap-4">
          <Icon name="AlertTriangle" size={40} className="text-red-400" />
          <p className="text-text-secondary">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="text-accent hover:underline text-sm flex items-center gap-1"
          >
            <Icon name="ArrowLeft" size={14} /> {t('goBack')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{drawing?.drawing_name || t('drawingDetail')} — SiteProof</title>
      </Helmet>
      <Header />
      <main className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center gap-2 mb-5">
          <BackButton fallback="/drawings-plans" />
        </div>

        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold text-text-primary">
              {drawing?.drawing_name || t('drawingDetail')}
            </h1>
            <p className="text-sm text-text-secondary mt-0.5">
              {drawing?.drawing_number && <span className="mr-3">#{drawing?.drawing_number}</span>}
              {drawing?.discipline && <span className="mr-3">{drawing?.discipline}</span>}
              {drawing?.revision && <span>{t('rev')} {drawing?.revision}</span>}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <DrawingViewerPanel
              drawingUrl={drawingUrl}
              isPdf={isPdf}
              loading={false}
            />
            <RelatedSnags snags={snags} loading={loadingSnags} />
          </div>

          <div className="space-y-6">
            <MetadataPanel drawing={drawing} />
            <RevisionHistory
              versions={versions}
              loading={loadingVersions}
              currentDrawingId={drawingId}
            />
            <UploadRevision
              drawing={drawing}
              onRevisionUploaded={handleRevisionUploaded}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default DrawingDetail;
